// מודל השיחה בצד לקוח (11 §1–2) — טהור (בלי DOM/רשת), כדי שייבדק בלי דפדפן: התמליל נבנה מהיסטוריה + אירועי SSE
// באותה פונקציה (applyEvent), seq ≤ lastSeq נזרק (replay בלי כפילויות); הודעה באמצע תור מאוחדת לפי sayId.
import type { SseEvent, Suggestion, Usage } from '@aibify/core';
import { parseSseEvent, type Attachment, type HistoryResponse } from './aiContract.ts';

export type Part =
  | { kind: 'text'; text: string }
  | { kind: 'tool'; name: string; summaryHe: string; ok: boolean | null; resultHe: string | null; secretKey: string | null; noResult?: boolean }
  | { kind: 'block'; block: unknown; asOf: string | null }
  /** קובץ שהסוכן שלח למשתמש (chat.sendFile / קבצי ריצת ניסיון) — כרטיס הורדה, תצוגה לתמונה/PDF/שמע/וידאו */
  | { kind: 'file'; file: Attachment; captionHe: string | null }
  | { kind: 'consent'; consentId: string; status: string }
  /** 20: כלי מייל נתקל ב-no_mailbox — "צריך לחבר Gmail" (כפתור שפותח את ה-drawer; המסך מציג "מחובר" כשהחשבון מחובר) */
  | { kind: 'mail_connect'; caps: Array<'send' | 'read'> }
  | { kind: 'notice'; noticeKind: string; textHe: string }
  | { kind: 'error'; textHe: string }
  /** הודעת המשתמש באמצע התור, במקום שנשלחה; heard — הסוכן קיבל אותה לתוך התור; attachments — הקבצים שצורפו לה */
  | { kind: 'user'; sayId: string; text: string; heard: boolean; attachments: Attachment[] };

export type TurnView = {
  id: string;
  userText: string;
  /** קבצים שצורפו להודעת הפתיחה (10 §1). */
  attachments: Attachment[];
  /** queued/running/done/... מהשרת; 'stopping' — לחצנו עצור ומחכים ל-done {canceled}. */
  status: string;
  parts: Part[];
  usage: Usage | null;
  /** 15 "להמשך": שלושה צעדים מהמודל (turn.suggest); null = אין (המסך לא ממציא). האחרון בתור גובר. */
  suggestions: Suggestion[] | null;
  /** תור המשך: נפתח מהודעות באמצע התור הזה שהסוכן לא קלט (queued.followUpOf). */
  followUpOf: string | null;
  /**
   * 11 (2026-10-16): sys.turn started_at/finished_at (ISO) — מה-done או מההיסטוריה, לא משעון הדפדפן.
   * "עבד 1:47 דק'" = finishedAt − startedAt; "לפני 3 דקות" = עכשיו − finishedAt. null — לא ידוע (תור חי / לא התחיל).
   */
  startedAt: string | null;
  finishedAt: string | null;
};

export type S = { turns: TurnView[]; lastSeq: number };
export type A =
  | { t: 'reset'; turns: TurnView[]; lastSeq: number }
  | { t: 'event'; seq: number; turnId?: string; ev: SseEvent; secretKey?: string }
  | { t: 'addTurn'; id: string; userText: string; attachments: Attachment[] }
  | { t: 'said'; id: string; sayId: string; text: string; attachments: Attachment[] }
  | { t: 'stopping'; id: string };

const LIVE = new Set(['queued', 'running', 'stopping']);
export const isLive = (t: TurnView) => LIVE.has(t.status);

function applyToTurn(turn: TurnView, ev: SseEvent, secretKey?: string): TurnView {
  const parts = [...turn.parts];
  const last = parts.at(-1);
  switch (ev.type) {
    case 'queued':
      // הודעת הפתיחה מגיעה גם באירוע (תור המשך / לשונית אחרת) — כשהמסך עוד לא מכיר אותה
      return {
        ...turn,
        status: turn.status === 'stopping' ? 'stopping' : 'queued',
        userText: turn.userText || ev.userText || '',
        attachments: turn.attachments.length ? turn.attachments : (ev.attachments ?? []),
        followUpOf: turn.followUpOf ?? ev.followUpOf ?? null,
      };
    case 'started':
      return { ...turn, status: turn.status === 'stopping' ? 'stopping' : 'running' };
    case 'text':
      if (last?.kind === 'text') parts[parts.length - 1] = { ...last, text: last.text + ev.delta };
      else parts.push({ kind: 'text', text: ev.delta });
      break;
    case 'tool_use':
      parts.push({ kind: 'tool', name: ev.name, summaryHe: ev.summaryHe, ok: null, resultHe: null, secretKey: null });
      break;
    case 'tool_result': {
      // סוגר את ה-tool_use הפתוח האחרון באותו שם; סוד — רק מפתח למפה שמחוץ ל-state
      let i = parts.length - 1;
      while (i >= 0 && !(parts[i]!.kind === 'tool' && (parts[i] as { name: string }).name === ev.name && (parts[i] as { ok: boolean | null }).ok === null)) i--;
      const res = { ok: ev.ok, resultHe: secretKey ? null : ev.summaryHe, secretKey: secretKey ?? null };
      if (i >= 0) parts[i] = { ...(parts[i] as Extract<Part, { kind: 'tool' }>), ...res };
      else parts.push({ kind: 'tool', name: ev.name, summaryHe: ev.name, ...res });
      break;
    }
    case 'block':
      parts.push({ kind: 'block', block: ev.block, asOf: ev.asOf ?? null });
      break;
    case 'file':
      if (!parts.some((p) => p.kind === 'file' && p.file.id === ev.file.id)) parts.push({ kind: 'file', file: ev.file, captionHe: ev.captionHe ?? null });
      break;
    case 'consent_pending':
      parts.push({ kind: 'consent', consentId: ev.consentId, status: 'pending' });
      break;
    case 'consent_decided': {
      const i = parts.findIndex((p) => p.kind === 'consent' && p.consentId === ev.consentId);
      if (i >= 0) parts[i] = { kind: 'consent', consentId: ev.consentId, status: ev.status };
      else parts.push({ kind: 'consent', consentId: ev.consentId, status: ev.status });
      break;
    }
    case 'mail_connect':
      parts.push({ kind: 'mail_connect', caps: ev.caps });
      break;
    case 'notice':
      parts.push({ kind: 'notice', noticeKind: ev.kind, textHe: ev.textHe });
      break;
    case 'error':
      parts.push({ kind: 'error', textHe: ev.textHe });
      break;
    case 'user_message':
      if (!parts.some((p) => p.kind === 'user' && p.sayId === ev.sayId)) parts.push({ kind: 'user', sayId: ev.sayId, text: ev.text, heard: false, attachments: ev.attachments ?? [] });
      break;
    case 'heard': {
      const i = parts.findIndex((p) => p.kind === 'user' && p.sayId === ev.sayId);
      if (i >= 0) parts[i] = { ...(parts[i] as Extract<Part, { kind: 'user' }>), heard: true };
      break;
    }
    case 'done':
      // כלי שנשאר "רץ" אחרי שהתור נגמר (tool_result שלא הגיע) — "בלי תוצאה", לא נקודה מהבהבת לנצח
      return { ...turn, parts: closeRunningTools(parts), status: ev.status, usage: ev.usage, startedAt: ev.startedAt ?? turn.startedAt, finishedAt: ev.finishedAt ?? turn.finishedAt };
    case 'heartbeat':
      return turn;
    case 'suggestions':
      return { ...turn, suggestions: ev.items };
  }
  return { ...turn, parts };
}

function closeRunningTools(parts: Part[]): Part[] {
  return parts.map((p) => (p.kind === 'tool' && p.ok === null && !p.noResult ? { ...p, noResult: true } : p));
}

const blankTurn = (id: string, userText = '', attachments: Attachment[] = []): TurnView => ({ id, userText, attachments, status: 'queued', parts: [], usage: null, suggestions: null, followUpOf: null, startedAt: null, finishedAt: null });

export function reducer(s: S, a: A): S {
  switch (a.t) {
    case 'reset':
      return { turns: a.turns, lastSeq: a.lastSeq };
    case 'addTurn': {
      const i = s.turns.findIndex((t) => t.id === a.id);
      // כבר הגיע מה-SSE (queued לפני תשובת ה-POST) — רק משלימים את הטקסט אם חסר
      if (i >= 0) return s.turns[i]!.userText || !a.userText ? s : { ...s, turns: s.turns.map((t, j) => (j === i ? { ...t, userText: a.userText, attachments: t.attachments.length ? t.attachments : a.attachments } : t)) };
      return { ...s, turns: [...s.turns, blankTurn(a.id, a.userText, a.attachments)] };
    }
    case 'said': {
      const ev: SseEvent = { type: 'user_message', sayId: a.sayId, text: a.text, midTurn: true, attachments: a.attachments };
      // התור שהשרת הכניס אליו את ההודעה לא מוכר למסך (למשל נפתח בינתיים) — נוסף, כדי שההודעה לא תיעלם
      if (!s.turns.some((t) => t.id === a.id)) return { ...s, turns: [...s.turns, applyToTurn({ ...blankTurn(a.id), status: 'running' }, ev)] };
      return { ...s, turns: s.turns.map((t) => (t.id === a.id ? applyToTurn(t, ev) : t)) };
    }
    case 'stopping':
      return { ...s, turns: s.turns.map((t) => (t.id === a.id && isLive(t) ? { ...t, status: 'stopping' } : t)) };
    case 'event': {
      if (a.seq <= s.lastSeq) return s; // כבר נראה (replay אחרי reconnect)
      if (a.ev.type === 'heartbeat') return { ...s, lastSeq: a.seq };
      let idx = a.turnId ? s.turns.findIndex((t) => t.id === a.turnId) : s.turns.length - 1;
      let turns = s.turns;
      if (idx < 0) {
        turns = [...turns, blankTurn(a.turnId ?? `t${a.seq}`)];
        idx = turns.length - 1;
      }
      turns = turns.map((t, i) => (i === idx ? applyToTurn(t, a.ev, a.secretKey) : t));
      return { turns, lastSeq: a.seq };
    }
  }
}

export function fromHistory(h: HistoryResponse): S {
  let s: S = { turns: [], lastSeq: 0 };
  for (const t of h.turns) {
    s = reducer(s, { t: 'addTurn', id: t.id, userText: t.userText, attachments: t.attachments });
    for (const raw of t.events) {
      const ev = parseSseEvent(raw);
      if (ev) s = reducer(s, { t: 'event', seq: raw.seq, turnId: t.id, ev });
    }
    // סטטוס התור מהשרת הוא האמת (תור שהסתיים בלי done בהיסטוריה וכדומה)
    s = { ...s, turns: s.turns.map((x) => (x.id === t.id && isLive(x) ? { ...x, status: t.status } : x)) };
    // זמני התור משורת sys.turn (גם לתורים שה-done שלהם לא נשא אותם — לפני 2026-10-16, או sweep)
    s = { ...s, turns: s.turns.map((x) => (x.id === t.id ? { ...x, startedAt: t.startedAt ?? x.startedAt, finishedAt: t.finishedAt ?? x.finishedAt } : x)) };
    // תור שנגמר: כלים שנשארו "רצים" — בלי תוצאה
    s = { ...s, turns: s.turns.map((x) => (x.id === t.id && !isLive(x) ? { ...x, parts: closeRunningTools(x.parts) } : x)) };
  }
  return { ...s, lastSeq: h.lastSeq };
}

